// packages import
import { Button, CircularProgress, DialogActions, Typography } from '@mui/material'
import { FC, useContext } from 'react'
// component import
import { Alert } from '../common/Alert'
import { CustomModal } from '../common/CustomModal'
/// others
import { useDeleteExternalUserMutation } from '../../../generated'
import { CANCEL, SOMETHING_WENT_WRONG } from '../../constants'
import { AppContext } from '../../context'
import { ActionType } from '../../context/AppContextReducer'
import { DispatchAndState } from '../../interfaceTypes'
import { titleize } from '../../utils'
import { ExternalUserAction, ExternalUserActionType, ExternalUserState } from './externalUserReducer'

export const RemoveExternalUser: FC<DispatchAndState<ExternalUserState, ExternalUserAction>> = ({
  state,
  dispatch: externalUserDispatch,
  refetch,
  roleType
}): JSX.Element => {
  const { isDeleteOpen, dispatch } = useContext(AppContext)
  const { currentExternalUser } = state || {}
  const { firstName, lastName, id } = currentExternalUser || {}
  const fullName = `${firstName || ''} ${lastName || ''}`
  const externalUserType = titleize(roleType || 'External User')

  const [removeExternalUser, { loading }] = useDeleteExternalUserMutation({
    variables: {
      deleteExternalUser: {
        id: id || ''
      }
    },

    onError({ message }) {
      if (message === 'Bad Request Exception') {
        Alert.error('Cannot delete external user with associated records.')
      } else {
        Alert.error(SOMETHING_WENT_WRONG)
      }
    },

    onCompleted(data) {
      if (data) {
        const { deleteExternalUser } = data
        const { response } = deleteExternalUser || {}
        const { status } = response || {}

        if (status === 200) {
          refetch && refetch()
          Alert.success(`${externalUserType} Deleted Successfully`)
          externalUserDispatch({ type: ExternalUserActionType.SET_CURRENT_EXTERNAL_USER, currentExternalUser: null })
          dispatch({ type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: false })
        }
      }
    }
  })

  const handleClose = () => dispatch({ type: ActionType.SET_IS_DELETE_OPEN, isDeleteOpen: false })

  const handleDelete = () => {
    handleClose()
    removeExternalUser()
  }

  return (
    <CustomModal title={`Delete ${externalUserType}`} isOpen={isDeleteOpen} handleClose={handleClose}>
      <Typography variant="body1">{`You are about to delete this ${externalUserType.toLowerCase()} having name:`}</Typography>

      <Typography variant="body1" style={{ display: 'inline-block' }} fontWeight={500}>
        {fullName || ''}{' '}
      </Typography>

      <DialogActions sx={{ padding: '30px 0 0', flexWrap: 'wrap', gap: '10px' }}>
        <Button onClick={handleClose} variant="outlined" aria-label={CANCEL}>
          {CANCEL}
        </Button>

        <Button
          disabled={loading}
          onClick={handleDelete}
          color="primary"
          variant="contained"
          endIcon={loading && <CircularProgress size={20} color="inherit" />}
          aria-label={`Delete ${externalUserType}`}
        >
          Delete {externalUserType}
        </Button>
      </DialogActions>
    </CustomModal>
  )
}
